import { createContext, useContext, ReactNode } from 'react';
import {
  usePeriodClosure,
  isDateInClosedPeriod as checkIsDateInClosedPeriod,
} from '@/api/periodClosure/periodClosure.api';
import { PeriodClosure } from '@/api/periodClosure/periodClosure.model';

// Helper to parse date string without timezone conversion
const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

interface PeriodClosureContextType {
  periodClosure: PeriodClosure | null;
  isLoading: boolean;
  isDateInClosedPeriod: (date: Date | null | undefined) => boolean;
  getClosedDate: () => Date | null;
}

const PeriodClosureContext = createContext<PeriodClosureContextType | undefined>(undefined);

export const PeriodClosureProvider = ({ children }: { children: ReactNode }) => {
  const { data: periodClosure, isLoading } = usePeriodClosure();

  const isDateInClosedPeriod = (date: Date | null | undefined): boolean => {
    return checkIsDateInClosedPeriod(date, periodClosure || null);
  };

  const getClosedDate = (): Date | null => {
    return periodClosure ? parseLocalDate(periodClosure.closedUntilDate) : null;
  };

  return (
    <PeriodClosureContext.Provider
      value={{
        periodClosure: periodClosure || null,
        isLoading,
        isDateInClosedPeriod,
        getClosedDate,
      }}
    >
      {children}
    </PeriodClosureContext.Provider>
  );
};

export const usePeriodClosureContext = () => {
  const context = useContext(PeriodClosureContext);
  if (context === undefined) {
    throw new Error('usePeriodClosureContext must be used within a PeriodClosureProvider');
  }
  return context;
};
